import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, use, useCallback, useEffect, useMemo, type ReactNode } from "react";
import { api, onAuthEvent, refreshSession, tokenStore, unwrap } from "@/api/client";
import { qk } from "@/api/keys";
import type { CurrentUser, Membership, OrganizationRole, OrganizationSummary } from "@/api/types";
import { meQueryOptions } from "./me";
import { can as roleCan, isReadOnly as computeReadOnly, type Permission } from "./rbac";

export interface SessionValue {
  /** Undefined while the first /v1/me is in flight. Null when nobody is signed in. */
  me: CurrentUser | null | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;

  memberships: Membership[];
  activeOrg: OrganizationSummary | undefined;
  /** The org id every tenant-scoped query key must be built from. */
  activeOrgId: string | undefined;
  role: OrganizationRole | undefined;

  /** True when the session may look but not touch: suspended org, or staff impersonation. */
  readOnly: boolean;

  /**
   * The single question the UI asks before rendering any control that writes.
   * Folds the read-only session state into the role matrix, so a suspended org
   * cannot render a Save button that the server would refuse anyway.
   */
  can: (permission: Permission) => boolean;

  switchOrg: (orgId: string) => Promise<void>;
  isSwitching: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<unknown>;
}

const SessionContext = createContext<SessionValue | null>(null);

/** How long before `expires_at` the proactive refresh fires — comfortably inside the
 * backend's 1-hour access token life, so it lands long before the 401 interceptor would
 * ever need to. */
const REFRESH_SKEW_MS = 5 * 60_000;
/** A floor under the delay, so a clock skew or a stale `expires_at` cannot turn into a
 * refresh-on-every-render loop. Small enough that a genuinely-about-to-expire token still
 * gets refreshed almost immediately rather than being pushed out to the next tick. */
const MIN_REFRESH_DELAY_MS = 2_000;

/**
 * Pure on purpose: exported so the scheduling arithmetic is testable without a component,
 * a timer, or a network — `expiresAt` in, milliseconds-from-`now` out.
 */
export function computeRefreshDelayMs(expiresAt: string, now: number): number {
  return Math.max(new Date(expiresAt).getTime() - now - REFRESH_SKEW_MS, MIN_REFRESH_DELAY_MS);
}

const WRITE_PERMISSIONS = new Set<Permission>([
  "org:update",
  "members:manage",
  "members:grantOwner",
  "invitations:manage",
  "sites:write",
  "sites:delete",
  "refills:create",
  "refills:update",
  "refills:delete",
  "requests:create",
  "requests:decide",
  "apiKeys:manage",
  "devices:manage",
  "firmware:upload",
]);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // The SAME query options the route guards use — one cache entry, one truth.
  const meQuery = useQuery(meQueryOptions);

  const switchMutation = useMutation({
    mutationFn: async (orgId: string) => {
      // Switching organization does NOT mint a credential, and the response carries no
      // token — we keep the one we already hold.
      //
      // This used to read `session.access_token` and store it. The endpoint used to
      // return a full Session whose token fields the schema marked REQUIRED, so the
      // server filled them with empty strings — and this line dutifully overwrote a
      // perfectly good token with "". Switching org signed you out. Every time.
      //
      // Both halves are fixed now: the server returns ActiveOrganization (no token,
      // because switching org changes WHICH TENANT you are acting in, not WHO YOU ARE),
      // and the field is gone from the generated type, so this cannot regress without
      // failing the build.
      return unwrap(
        api.POST("/v1/organizations/{orgId}/switch", { params: { path: { orgId } } }),
      );
    },
    onSuccess: async () => {
      // The org-scoped keys mean the previous org's rows are still in the cache, but they
      // are unreachable: nothing will ever build a key that points at them again. We only
      // need /v1/me — the new active org — to be authoritative before the next render.
      await queryClient.invalidateQueries({ queryKey: qk.me() });
    },
  });

  const signOutMutation = useMutation({
    mutationFn: async () => {
      try {
        await unwrap(api.POST("/v1/auth/logout", {}));
      } finally {
        // Even if the server call fails, this browser is done with the session.
        tokenStore.set(null);
      }
    },
    onSuccess: () => {
      // Signing out is the one moment the whole cache must go: the next person at this
      // keyboard must not be able to see the last person's tenant data by pressing Back.
      queryClient.clear();
    },
  });

  // PREFER THE CACHE OVER THE SNAPSHOT.
  //
  // `meQuery.data` is a React snapshot of the query cache, taken at the last render. The
  // cache itself can be a tick ahead of it — and in this app it routinely is, because the
  // things that change identity (the route guard's /switch, creating an organization) write
  // the cache and navigate in the same tick, before the provider above has re-rendered.
  //
  // That one tick has now caused two separate P0s:
  //   - every authenticated route threw after sign-in ("a tenant-scoped hook ran with no
  //     active organization"), and the error boundary LATCHED, so the data arriving a
  //     millisecond later did not heal the screen;
  //   - creating your organization bounced you straight back to the create-organization
  //     page you had just completed, forever.
  //
  // Both were the same bug wearing different clothes, and patching them at each call site
  // would only have left a third one waiting. So the session reads the cache it is derived
  // FROM. This is not a second source of truth — `getQueryData` and `meQuery.data` are the
  // same entry; one is simply not stale. The observer still drives re-renders; we just do
  // not insist on reading yesterday's copy of it.
  const snapshot = meQuery.data;
  const cached = queryClient.getQueryData<CurrentUser | null>(qk.me());
  const me = cached ?? snapshot;

  const activeOrg = me?.active_organization;
  const memberships = useMemo(() => me?.memberships ?? [], [me]);

  // The role is a property of the MEMBERSHIP, never of the user: the same person is an
  // owner of one organization and a viewer of another.
  const role = useMemo<OrganizationRole | undefined>(() => {
    if (!activeOrg) return undefined;
    const membership = memberships.find((m) => m.organization.id === activeOrg.id);
    return membership?.role ?? activeOrg.role;
  }, [activeOrg, memberships]);

  const readOnly = computeReadOnly({
    orgStatus: activeOrg?.status,
    impersonated: me?.impersonated,
  });

  const isAuthenticated = !!me;

  /**
   * KEEP THE ACCESS TOKEN ALIVE. The backend mints it for one hour; left alone, that is one
   * hour before every request 401s and the customer is bounced to sign-in mid-shift.
   *
   * This schedules a refresh a few minutes BEFORE `expires_at`, so the common case is "the
   * token was already fresh" rather than "a request just failed and is being retried". The
   * 401 interceptor in `client.ts` is the correctness net underneath this — if the tab was
   * backgrounded and this timer fired late (or never), that interceptor still catches it.
   * Both paths call the SAME `refreshSession()`, which is single-flight, so they can never
   * fire the single-use refresh token twice at once.
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = (expiresAt: string | null): void => {
      if (timer !== undefined) clearTimeout(timer);
      if (!expiresAt) return;

      const delay = computeRefreshDelayMs(expiresAt, Date.now());
      timer = setTimeout(() => {
        void refreshSession().then((refreshed) => {
          // A failed refresh already cleared the token and emitted "expired" (handled
          // below) — there is nothing left to reschedule.
          if (!cancelled && refreshed) schedule(refreshed.expiresAt);
        });
      }, delay);
    };

    schedule(tokenStore.getExpiresAt());

    const unsubscribe = onAuthEvent((event) => {
      if (event.type === "refreshed") {
        schedule(event.expiresAt);
        return;
      }

      // The refresh token is gone too (revoked, or already consumed elsewhere) — there is no
      // session left to keep alive. Write the cache directly rather than invalidating it.
      //
      // `invalidateQueries` would refetch `/v1/me` — which, with the session actually gone,
      // 401s again, which the interceptor below reacts to by trying to refresh AGAIN (the
      // one that just failed is no longer "in flight", so it is not deduped), which fails
      // and emits "expired" again, which invalidates again — a self-sustaining loop that
      // never touches the call stack (each turn is a fresh microtask) so nothing ever
      // overflows, it just allocates forever. `setQueryData` is a plain cache write: no
      // request, no 401, no next turn.
      if (timer !== undefined) clearTimeout(timer);
      queryClient.setQueryData(qk.me(), null);
    });

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      unsubscribe();
    };
  }, [isAuthenticated, queryClient]);

  const can = useCallback(
    (permission: Permission): boolean => {
      if (readOnly && WRITE_PERMISSIONS.has(permission)) return false;
      return roleCan(role, permission);
    },
    [role, readOnly],
  );

  const switchOrg = useCallback(
    async (orgId: string) => {
      await switchMutation.mutateAsync(orgId);
    },
    [switchMutation],
  );

  const signOut = useCallback(async () => {
    await signOutMutation.mutateAsync();
  }, [signOutMutation]);

  /**
   * ROTATE THE TOKEN. THIS IS NOT `meQuery.refetch()`.
   *
   * It used to be — which meant nothing calling `refresh()` ever actually renewed the
   * session; it just re-asked `/v1/me` with whatever access token was already about to
   * expire. That answered "who are you" without touching "how much longer are you signed
   * in", so an hour after login the very next request still 401'd.
   *
   * A caller that wants the session's identity re-read (org switched under it elsewhere,
   * say) should refetch `/v1/me` directly — that is a different question from this one.
   */
  const refresh = useCallback(async () => {
    const refreshed = await refreshSession();
    if (!refreshed) return null; // refresh token dead too — nothing left to renew.
    return meQuery.refetch();
  }, [meQuery]);

  const value = useMemo<SessionValue>(
    () => ({
      me,
      isLoading: meQuery.isPending,
      isAuthenticated,
      memberships,
      activeOrg,
      activeOrgId: activeOrg?.id,
      role,
      readOnly,
      can,
      switchOrg,
      isSwitching: switchMutation.isPending,
      signOut,
      refresh,
    }),
    [
      me,
      meQuery.isPending,
      isAuthenticated,
      memberships,
      activeOrg,
      role,
      readOnly,
      can,
      switchOrg,
      switchMutation.isPending,
      signOut,
      refresh,
    ],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionValue {
  const ctx = use(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}

/**
 * The org id, guaranteed. Every tenant-scoped hook calls this instead of reaching for an
 * optional id — a query that runs without an org is the bug class this whole file exists
 * to prevent, so it fails loudly rather than fetching something unscoped.
 */
export function useActiveOrgId(): string {
  const { activeOrgId } = useSession();
  const queryClient = useQueryClient();

  // THE FALLBACK BELONGS HERE, IN THE CONSUMER — AND ONLY EXPERIENCE TAUGHT ME THAT.
  //
  // `SessionProvider` also prefers the cache over its own snapshot, and that is correct, but
  // it is NOT sufficient: a provider only re-reads the cache WHEN IT RE-RENDERS. The router
  // commits this route's component as a child in the same tick the guard finishes writing
  // the cache — before the provider above has re-rendered — so the context handed down here
  // is still the memoized object from the last render, with no org in it.
  //
  // The component then threw, and React's error boundary LATCHED: the provider re-rendered a
  // millisecond later with perfectly good data, and the screen stayed dead. Which is why
  // every screen worked when you NAVIGATED to it and only the first render after sign-in was
  // broken — the signature of exactly this and nothing else.
  //
  // So read the cache here, in the thing that is actually rendering. Not a second source of
  // truth: `getQueryData` and the context are the same cache entry. One of them is simply
  // not a tick old.
  const fromCache = queryClient.getQueryData<CurrentUser | null>(qk.me())?.active_organization
    ?.id;

  const orgId = activeOrgId ?? fromCache;
  if (!orgId) {
    throw new Error(
      "A tenant-scoped hook ran with no active organization. The route guard should have " +
        "redirected to onboarding before this rendered.",
    );
  }
  return orgId;
}
